import { friendlyViewName as outboundFriendlyViewName } from "@redotech/redo-model/views/default-outbound-views";
import { friendlyViewName as purchaseOrderFriendlyViewName } from "@redotech/redo-model/views/default-purchase-order-views";
import { friendlyViewName as returnFriendlyViewName } from "@redotech/redo-model/views/default-return-views";
import { friendlyShipmentViewName } from "@redotech/redo-model/views/default-shipment-views";
import { ClientFulfillmentGroupFolder } from "@redotech/redo-model/views/folders";
import {
  ViewTypesNames,
  viewTypes,
  viewWithIdTypes,
} from "@redotech/redo-model/views/views";
import {
  FlattenedItem,
  TreeFolderItem,
  TreeItem,
  TreeItemType,
  TreeViewItem,
} from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";

// Todo: also make folder generic
export type FolderWithView<T extends keyof viewTypes> = {
  folder: ClientFulfillmentGroupFolder;
  views: viewWithIdTypes[T][];
};

export function viewsAndFoldersToTree<T extends keyof viewTypes>({
  viewsWithoutFolders,
  foldersWithViews,
  urlPrefix,
  viewCounts,
  folderCounts,
  hideZeroNotifications,
}: {
  viewsWithoutFolders: viewWithIdTypes[T][];
  foldersWithViews: FolderWithView<T>[];
  urlPrefix: string;
  viewCounts?: Record<string, number>;
  folderCounts?: Record<string, any>;
  hideZeroNotifications: boolean;
}) {
  const allItemsWithPositions = [
    ...viewsWithoutFolders.map((view) => ({
      ...view,
      position: view.position,
      _id: view._id,
    })),
    ...foldersWithViews.map((folder) => ({
      ...folder,
      views: folder.views.sort((a, b) => a.position - b.position),
      position: folder.folder.position,
      _id: folder.folder._id,
    })),
  ];
  const sortedItems = allItemsWithPositions.sort((a, b) => {
    return a.position - b.position;
  });

  const treeItems: TreeItem[] = sortedItems.map((item) => {
    if ("folder" in item) {
      return folderToTreeItem({
        folder: item,
        urlPrefix,
        folderCounts,
        viewCounts,
        hideZeroNotifications,
      });
    } else {
      return viewToTreeItem({
        view: item,
        urlPrefix,
        viewCounts,
        hideZeroNotifications,
      });
    }
  });

  return treeItems;
}

function viewToTreeItem<T extends keyof viewTypes>({
  view,
  urlPrefix,
  viewCounts,
  hideZeroNotifications,
}: {
  view: viewWithIdTypes[T];
  urlPrefix: string;
  viewCounts?: Record<string, number>;
  hideZeroNotifications: boolean;
}): TreeViewItem {
  let friendlyName = view.name;
  switch (view.table) {
    case ViewTypesNames.RETURNS:
    case ViewTypesNames.CLAIMS:
    case ViewTypesNames.WARRANTIES:
    case ViewTypesNames.RECYCLE:
      friendlyName = returnFriendlyViewName(view as viewWithIdTypes["returns"]);
      break;
    case ViewTypesNames.FULFILLMENT_GROUPS:
      friendlyName = outboundFriendlyViewName(view);
      break;
    case ViewTypesNames.PURCHASE_ORDERS:
      friendlyName = purchaseOrderFriendlyViewName(view);
      break;
    case ViewTypesNames.SHIPMENTS:
      friendlyName = friendlyShipmentViewName(view.name);
      break;
  }

  return {
    id: view._id,
    name: view.name,
    actionHref: `${urlPrefix}/${encodeURIComponent(view.name)}`,
    friendlyName,
    type: TreeItemType.View,
    notificationCount:
      viewCounts?.[view?._id] === 0 && hideZeroNotifications
        ? undefined
        : viewCounts?.[view?._id],
  };
}

function folderToTreeItem<T extends keyof viewTypes>({
  folder,
  urlPrefix,
  folderCounts,
  viewCounts,
  hideZeroNotifications,
}: {
  folder: FolderWithView<T>;
  urlPrefix: string;
  folderCounts?: Record<string, number>;
  viewCounts?: Record<string, number>;
  hideZeroNotifications: boolean;
}): TreeFolderItem {
  return {
    id: folder.folder._id,
    name: folder.folder.name,
    friendlyName: folder.folder.name,
    type: TreeItemType.Folder,
    children: folder.views.map((view) =>
      viewToTreeItem({ view, urlPrefix, viewCounts, hideZeroNotifications }),
    ),
    notificationCount:
      folderCounts?.[folder?.folder?._id] === 0 && hideZeroNotifications
        ? undefined
        : folderCounts?.[folder?.folder?._id],
  };
}

export function treeToViewsAndFolders<T extends keyof viewTypes>(
  flattenedItems: FlattenedItem[],
  allViews: viewWithIdTypes[T][],
  allFolders: ClientFulfillmentGroupFolder[],
) {
  const viewsCopy = allViews.map((view) => deepCopyObject(view));
  const foldersCopy = allFolders.map((folder) => deepCopyObject(folder));

  const newFolders: ClientFulfillmentGroupFolder[] = [];
  const newViews: viewWithIdTypes[T][] = [];

  for (const item of flattenedItems) {
    switch (item.type) {
      case TreeItemType.View: {
        const matchingView = viewsCopy.find((view) => view._id === item.id);
        if (matchingView) {
          if (item.parentId) {
            matchingView.folderId = item.parentId.toString();
          } else {
            matchingView.folderId = null;
          }
          matchingView.position = item.index;
          newViews.push(matchingView);
        }
        break;
      }
      case TreeItemType.Folder: {
        const matchingFolder = foldersCopy.find(
          (folder) => folder._id === item.id,
        );

        if (matchingFolder) {
          matchingFolder.position = item.index;
          newFolders.push(matchingFolder);
        }
        break;
      }
      default:
        assertNever(item);
    }
  }

  return { views: newViews, folders: newFolders };
}
